// Temporary adapter layer for legacy shared calculator engines.
// These scripts are side-effect imported and attach engine objects to globalThis.
// @ts-ignore Legacy UMD module with global side effects.
import '../../../assets/js/calculator-engines/fire-calculator-engine.js';
// @ts-ignore Legacy UMD module with global side effects.
import '../../../assets/js/calculator-engines/advanced-fire-engine.js';
// @ts-ignore Legacy UMD module with global side effects.
import '../../../assets/js/calculator-engines/coast-fire-engine.js';
// @ts-ignore Legacy UMD module with global side effects.
import '../../../assets/js/calculator-engines/compound-interest-engine.js';
// @ts-ignore Legacy UMD module with global side effects.
import '../../../assets/js/calculator-engines/retirement-goal-engine.js';
// @ts-ignore Legacy UMD module with global side effects.
import '../../../assets/js/calculator-engines/budget-planner-engine.js';
// @ts-ignore Legacy UMD module with global side effects.
import '../../../assets/js/calculator-engines/debt-payoff-engine.js';
// @ts-ignore Legacy UMD module with global side effects.
import '../../../assets/js/calculator-engines/emergency-fund-engine.js';
// @ts-ignore Legacy UMD module with global side effects.
import '../../../assets/js/calculator-engines/mortgage-engine.js';
// @ts-ignore Legacy UMD module with global side effects.
import '../../../assets/js/calculator-engines/paycheck-engine.js';
// @ts-ignore Legacy UMD module with global side effects.
import '../../../assets/js/calculator-engines/rent-vs-buy-engine.js';

type FireInput = {
  currentAge: number;
  retirementAge: number;
  growthRate: number;
  contributionIncrease?: number;
  contributionFrequency: 'monthly' | 'yearly';
  initialBalances: number[];
  contributions: number[];
};

type FireYear = { year: number; age: number; balances: number[]; total: number };

type FireResult = {
  normalizedContribution401k: number;
  years: FireYear[];
};

type AdvancedInput = {
  initial401k: number;
  initialRoth: number;
  initialTaxable: number;
  contribution401k: number;
  contributionRoth: number;
  contributionTaxable: number;
  contributionFrequency: 'monthly' | 'yearly';
  preRetirementGrowth: number;
  postRetirementGrowth: number;
  currentAge: number;
  retirementAge: number;
  lifeExpectancy: number;
  withdrawalRate: number;
  taxRate: number;
  inflationRate: number;
  contributionIncrease: number;
  penaltyFreeAge?: number;
  bracketCapGross?: number;
  bracketCapGrowth?: number;
};

type AdvancedYear = {
  year: number;
  age: number;
  balances: number[];
  total: number;
  withdrawn: number;
  withdrawalTarget: number;
  withdrawalShortfall: number;
  withdrawalFrom401k: number;
  withdrawalFromRoth: number;
  withdrawalFromTaxable: number;
};

type AdvancedResult = {
  normalizedContribution401k: number;
  /** Portfolio total carried into the retirement year, before that year's growth and first withdrawal. */
  retirementBalance: number;
  /** Gross first-year withdrawal: withdrawalRate applied to retirementBalance. */
  baseWithdrawal: number;
  years: AdvancedYear[];
};

type CoastInput = {
  currentAge: number;
  retirementAge: number;
  expectedReturn: number;
  inflationRate: number;
  annualExpenses: number;
  withdrawalRate: number;
  targetFIRENumber: number;
  targetMethod: 'expenses' | 'number';
  currentSavings: number;
};

type CoastResult = {
  yearsToRetirement: number;
  realReturn: number;
  calculatedFIRENumber: number;
  coastFIRENumber: number;
  projectedBalance: number;
  isCoastFIRE: boolean;
  yearsToCoastFIRE: number;
  monthlySavingsNeeded: number;
  savingsWindowMonths: number;
  hasSavingsWindow: boolean;
  effectiveWithdrawalRate: number;
  chart: {
    labels: number[];
    projectionData: number[];
    fireTargetLine: number[];
  };
};

type CompoundInterestInput = {
  principal: number;
  monthlyContribution: number;
  interestRate: number;
  years: number;
  compoundFrequency: string;
};

type CompoundInterestYear = {
  year: number;
  startBalance: number;
  contributions: number;
  interest: number;
  endBalance: number;
};

type CompoundInterestResult = {
  finalBalance: number;
  totalContributions: number;
  totalInterest: number;
  ruleOf72: number | null;
  yearlyData: CompoundInterestYear[];
  chart: {
    labels: number[];
    balanceHistory: number[];
    contributionHistory: number[];
    interestHistory: number[];
  };
};

type RetirementGoalInput = {
  retirementGoal: number;
  currentSavings: number;
  currentAge: number;
  retirementAge: number;
  expectedReturn: number;
};

type RetirementGoalYear = {
  year: number;
  age: number;
  startBalance: number;
  contributions: number;
  growth: number;
  endBalance: number;
};

type RetirementGoalMilestone = {
  percent: number;
  amount: number;
  year: number;
};

type RetirementGoalResult = {
  yearsToRetirement: number;
  requiredMonthlySavings: number;
  projectedFromCurrent: number;
  alreadyOnTrack: boolean;
  totalContributions: number;
  totalGrowth: number;
  shortfall: number;
  scenarios: Array<{ rate: number; monthly: number }>;
  yearlyData: RetirementGoalYear[];
  milestones: RetirementGoalMilestone[];
  chart: {
    labels: number[];
    balances: number[];
    contributions: number[];
    goalLine: number[];
  };
};

type BudgetPlannerInput = {
  incomes: Array<{ amount: number }>;
  expenses: Array<{ category: string; amount: number }>;
  categories: Array<{ id: string; type: 'needs' | 'wants' | 'savings' }>;
};

type BudgetPlannerResult = {
  totalIncome: number;
  totalExpenses: number;
  remaining: number;
  needsTotal: number;
  wantsTotal: number;
  savingsTotal: number;
  needsPercent: number;
  wantsPercent: number;
  savingsPercent: number;
  categoryTotals: Record<string, number>;
};

type DebtPayoffDebt = {
  name: string;
  balance: number;
  rate: number;
  minPayment: number;
};

type DebtPayoffInput = {
  debts: DebtPayoffDebt[];
  extraPayment: number;
};

type DebtPayoffStrategy = {
  months: number;
  totalInterest: number;
  /** True when every balance reached zero within the cap; false means interest outpaces payments. */
  paidOff?: boolean;
  /** Total balance remaining at exit (0 when paidOff). */
  remainingBalance?: number;
  /**
   * `month` and `paidOffMonth` are null for a debt that never pays off within the cap.
   * `paidOffMonth` is the canonical field; `month` is retained for backward compatibility.
   */
  debtPayoffs: Array<{ name: string; month: number | null; paidOffMonth: number | null }>;
  monthlyData: Array<{ month: number; totalBalance: number }>;
};

type DebtPayoffResult = {
  minimum: DebtPayoffStrategy;
  avalanche: DebtPayoffStrategy;
  snowball: DebtPayoffStrategy;
};

type EmergencyFundInput = {
  expenses: {
    housing: number;
    utilities: number;
    groceries: number;
    transportation: number;
    insurance: number;
    debtPayments: number;
    other: number;
  };
  currentSavings: number;
  monthlyContribution: number;
  employment: 'stable' | 'contract' | 'self' | 'variable';
  incomeSources: 'single' | 'dual' | 'multiple';
  dependents: 'none' | 'some' | 'many';
  jobMarket: 'good' | 'hot' | 'tough';
};

type EmergencyFundResult = {
  totalMonthlyExpenses: number;
  recommendedMonths: number;
  targetEmergencyFund: number;
  currentMonthsCovered: number;
  progressPercent: number;
  timeToGoal: string;
  milestones: Array<{ months: number; amount: number; reached: boolean; monthsToReach: number | '∞' }>;
  chart: {
    labels: number[];
    savings: number[];
    targetLine: number[];
  };
};

type MortgageInput = {
  homePrice: number;
  downPayment: number;
  interestRate: number;
  loanTermYears: number;
  propertyTax: number;
  homeInsurance: number;
  pmi: number;
  hoa: number;
  extraPayment: number;
};

type MortgageResult = {
  loanAmount: number;
  downPaymentPercent: number;
  exactDownPaymentRatio: number;
  monthlyInterestRate: number;
  numberOfPayments: number;
  monthlyPI: number;
  monthlyTaxInsurance: number;
  totalMonthlyPayment: number;
  amortizationSchedule: Array<{
    year: number;
    principalPaid: number;
    interestPaid: number;
    endingBalance: number;
    equityPercent: string;
  }>;
  amortizationScheduleWithExtra: Array<{
    year: number;
    principalPaid: number;
    interestPaid: number;
    endingBalance: number;
    equityPercent: string;
  }>;
  totalPaid: number;
  totalInterest: number;
  totalInterestWithoutExtra: number;
  interestSaved: number;
  actualLoanTerm: number;
  payoffYear: number;
  chart: {
    labels: number[];
    principalData: number[];
    interestData: number[];
    balanceData: number[];
  };
};

type PaycheckInput = {
  salary: number;
  payFrequency: 'weekly' | 'biweekly' | 'semimonthly' | 'monthly';
  filingStatus: 'single' | 'married' | 'head';
  stateRate: number;
  contribution401kPercent: number;
  healthInsurance: number;
  hsa: number;
  otherPreTax: number;
};

type PaycheckResult = {
  periodsPerYear: number;
  grossPayPerPeriod: number;
  contribution401kAnnual: number;
  contribution401kPerPeriod: number;
  preTaxDeductionsPerPeriod: number;
  taxableIncomePerPeriod: number;
  taxableIncomeAnnual: number;
  standardDeduction: number;
  federalTaxableIncomeAnnual: number;
  federalMarginalRate: number;
  federalBracketLowerBound: number;
  federalBracketUpperBound: number;
  federalTaxAnnual: number;
  federalTaxPerPeriod: number;
  stateTaxableIncomeAnnual: number;
  additionalMedicareThreshold: number;
  stateTaxAnnual: number;
  stateTaxPerPeriod: number;
  socialSecurityAnnual: number;
  socialSecurityPerPeriod: number;
  medicareAnnual: number;
  medicarePerPeriod: number;
  totalTaxesPerPeriod: number;
  totalDeductionsPerPeriod: number;
  netPayPerPeriod: number;
  annualNetPay: number;
};

type RentVsBuyInput = {
  monthlyRent: number;
  rentIncrease: number;
  rentersInsurance: number;
  investmentReturn: number;
  homePrice: number;
  downPayment: number;
  interestRate: number;
  loanTermYears: number;
  propertyTax: number;
  homeInsurance: number;
  maintenancePercent: number;
  homeAppreciation: number;
  closingCostsBuy: number;
  sellingCostsPercent: number;
  timeHorizon: number;
};

type RentVsBuyResult = {
  loanAmount: number;
  monthlyMortgage: number;
  totalRentPaid: number;
  totalRentersInsurance: number;
  investmentGains: number;
  totalRentCost: number;
  totalMortgagePayments: number;
  totalPropertyTax: number;
  totalHomeInsurance: number;
  totalMaintenance: number;
  futureHomeValue: number;
  remainingLoanBalance: number;
  sellingCosts: number;
  /** Equity released by the sale before selling costs: future value minus remaining loan. */
  grossSaleValue: number;
  /** Cash in hand at closing: grossSaleValue minus selling costs. */
  saleProceeds: number;
  /** Every dollar the buy path spends, selling costs included. */
  totalBuyCost: number;
  netBuyCost: number;
  recommendation: 'rent' | 'buy';
  breakEvenYear: number | null;
  breakEvenComparison: { rentCost: number; buyCost: number } | null;
  chart: {
    labels: number[];
    rentCosts: number[];
    buyCosts: number[];
  };
};

type LegacyEngineGlobals = typeof globalThis & {
  FireCalculatorEngine?: { calculate: (input: FireInput) => FireResult };
  AdvancedFireEngine?: { calculate: (input: AdvancedInput) => AdvancedResult };
  CoastFireEngine?: { calculate: (input: CoastInput) => CoastResult };
  CompoundInterestEngine?: { calculate: (input: CompoundInterestInput) => CompoundInterestResult };
  RetirementGoalEngine?: { calculate: (input: RetirementGoalInput) => RetirementGoalResult };
  BudgetPlannerEngine?: { calculate: (input: BudgetPlannerInput) => BudgetPlannerResult };
  DebtPayoffEngine?: { calculate: (input: DebtPayoffInput) => DebtPayoffResult };
  EmergencyFundEngine?: { calculate: (input: EmergencyFundInput) => EmergencyFundResult };
  MortgageEngine?: { calculate: (input: MortgageInput) => MortgageResult };
  PaycheckEngine?: { calculate: (input: PaycheckInput) => PaycheckResult };
  RentVsBuyEngine?: { calculate: (input: RentVsBuyInput) => RentVsBuyResult };
};

function getFireEngine() {
  const engine = (globalThis as LegacyEngineGlobals).FireCalculatorEngine;
  if (!engine) {
    throw new Error('FireCalculatorEngine was not initialized.');
  }
  return engine;
}

function getAdvancedEngine() {
  const engine = (globalThis as LegacyEngineGlobals).AdvancedFireEngine;
  if (!engine) {
    throw new Error('AdvancedFireEngine was not initialized.');
  }
  return engine;
}

function getCoastEngine() {
  const engine = (globalThis as LegacyEngineGlobals).CoastFireEngine;
  if (!engine) {
    throw new Error('CoastFireEngine was not initialized.');
  }
  return engine;
}

function getCompoundEngine() {
  const engine = (globalThis as LegacyEngineGlobals).CompoundInterestEngine;
  if (!engine) {
    throw new Error('CompoundInterestEngine was not initialized.');
  }
  return engine;
}

function getRetirementGoalEngine() {
  const engine = (globalThis as LegacyEngineGlobals).RetirementGoalEngine;
  if (!engine) {
    throw new Error('RetirementGoalEngine was not initialized.');
  }
  return engine;
}

function getBudgetPlannerEngine() {
  const engine = (globalThis as LegacyEngineGlobals).BudgetPlannerEngine;
  if (!engine) {
    throw new Error('BudgetPlannerEngine was not initialized.');
  }
  return engine;
}

function getDebtPayoffEngine() {
  const engine = (globalThis as LegacyEngineGlobals).DebtPayoffEngine;
  if (!engine) {
    throw new Error('DebtPayoffEngine was not initialized.');
  }
  return engine;
}

function getEmergencyFundEngine() {
  const engine = (globalThis as LegacyEngineGlobals).EmergencyFundEngine;
  if (!engine) {
    throw new Error('EmergencyFundEngine was not initialized.');
  }
  return engine;
}

function getMortgageEngine() {
  const engine = (globalThis as LegacyEngineGlobals).MortgageEngine;
  if (!engine) {
    throw new Error('MortgageEngine was not initialized.');
  }
  return engine;
}

function getPaycheckEngine() {
  const engine = (globalThis as LegacyEngineGlobals).PaycheckEngine;
  if (!engine) {
    throw new Error('PaycheckEngine was not initialized.');
  }
  return engine;
}

function getRentVsBuyEngine() {
  const engine = (globalThis as LegacyEngineGlobals).RentVsBuyEngine;
  if (!engine) {
    throw new Error('RentVsBuyEngine was not initialized.');
  }
  return engine;
}

export function calculateFire(input: FireInput): FireResult {
  return getFireEngine().calculate(input);
}

export function calculateAdvancedFire(input: AdvancedInput): AdvancedResult {
  return getAdvancedEngine().calculate(input);
}

export function calculateCoastFire(input: CoastInput): CoastResult {
  return getCoastEngine().calculate(input);
}

export function calculateCompoundInterest(input: CompoundInterestInput): CompoundInterestResult {
  return getCompoundEngine().calculate(input);
}

export function calculateRetirementGoal(input: RetirementGoalInput): RetirementGoalResult {
  return getRetirementGoalEngine().calculate(input);
}

export function calculateBudgetPlanner(input: BudgetPlannerInput): BudgetPlannerResult {
  return getBudgetPlannerEngine().calculate(input);
}

export function calculateDebtPayoff(input: DebtPayoffInput): DebtPayoffResult {
  return getDebtPayoffEngine().calculate(input);
}

export function calculateEmergencyFund(input: EmergencyFundInput): EmergencyFundResult {
  return getEmergencyFundEngine().calculate(input);
}

export function calculateMortgage(input: MortgageInput): MortgageResult {
  return getMortgageEngine().calculate(input);
}

export function calculatePaycheck(input: PaycheckInput): PaycheckResult {
  return getPaycheckEngine().calculate(input);
}

export function calculateRentVsBuy(input: RentVsBuyInput): RentVsBuyResult {
  return getRentVsBuyEngine().calculate(input);
}

export type {
  FireInput,
  FireResult,
  FireYear,
  AdvancedInput,
  AdvancedResult,
  AdvancedYear,
  CoastInput,
  CoastResult,
  CompoundInterestInput,
  CompoundInterestResult,
  CompoundInterestYear,
  RetirementGoalInput,
  RetirementGoalResult,
  RetirementGoalYear,
  RetirementGoalMilestone,
  BudgetPlannerInput,
  BudgetPlannerResult,
  DebtPayoffInput,
  DebtPayoffResult,
  EmergencyFundInput,
  EmergencyFundResult,
  MortgageInput,
  MortgageResult,
  PaycheckInput,
  PaycheckResult,
  RentVsBuyInput,
  RentVsBuyResult
};
